import { useMemo, useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useGetMeQuery } from "../api/auth/auth.grpc";
import { useGetOrganizationQuery } from "../api/organization/organization.grpc";
import { useSelectedCompany } from "../store/company/hooks/useSelectedCompany";
import { normalizeOrganization, normalizeUser } from "@racoon/lib";
import { endSession } from "@racoon/api";

export function useAuthSession() {
  const { companyId } = useParams();
  const { selectedCompanyId } = useSelectedCompany();

  const {
    data: meData,
    isLoading: isMeLoading,
    error: meError,
    refetch: refetchMe,
  } = useGetMeQuery({});

  const activeCompanyId = useMemo(() => {
    if (companyId) {
      const parsed = Number(companyId);
      if (Number.isInteger(parsed) && parsed > 0) return parsed;
    }
    if (selectedCompanyId) {
      const parsed = Number(selectedCompanyId);
      if (Number.isInteger(parsed) && parsed > 0) return parsed;
    }
    if (meData?.user?.ownerOrganizationId) {
      const parsed = Number(meData.user.ownerOrganizationId);
      if (Number.isInteger(parsed) && parsed > 0) return parsed;
    }
    return null;
  }, [companyId, selectedCompanyId, meData?.user?.ownerOrganizationId]);

  const shouldFetchActiveOrg = useMemo(() => {
    if (!activeCompanyId) return false;
    const myOrgId = meData?.organization?.id ? Number(meData.organization.id) : null;
    return activeCompanyId !== myOrgId;
  }, [activeCompanyId, meData?.organization?.id]);

  const {
    data: fetchedOrg,
    isLoading: isOrgLoading,
    refetch: refetchOrg,
  } = useGetOrganizationQuery(
    { organizationId: BigInt(activeCompanyId ?? 0) },
    { skip: !shouldFetchActiveOrg },
  );

  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsImpersonating(!!window.localStorage.getItem("admin_backup_token"));
    }
  }, []);

  const stopImpersonate = useCallback(() => {
    const adminBackupToken = localStorage.getItem("admin_backup_token");
    if (adminBackupToken) {
      localStorage.setItem("auth_token", adminBackupToken);
      localStorage.setItem(
        "auth_token_type",
        localStorage.getItem("admin_backup_token_type") || "Bearer",
      );
      localStorage.setItem("auth_user", localStorage.getItem("admin_backup_user") || "{}");

      localStorage.removeItem("admin_backup_token");
      localStorage.removeItem("admin_backup_token_type");
      localStorage.removeItem("admin_backup_user");
      localStorage.removeItem("selected_company");

      window.location.replace("/organization-admin");
    } else {
      void endSession().finally(() => window.location.replace("/login"));
    }
  }, []);

  const user = useMemo(() => {
    return normalizeUser(meData?.user, meData?.organization);
  }, [meData?.user, meData?.organization]);

  const activeCompany = useMemo(() => {
    const resolved = shouldFetchActiveOrg ? fetchedOrg : meData?.organization;
    return normalizeOrganization(resolved);
  }, [shouldFetchActiveOrg, fetchedOrg, meData?.organization]);

  const isReceiverUser = useMemo(() => {
    const role = user?.role;
    if (!role) return false;
    return String(role).trim() === "USER_ROLE_RECEIVER_USER";
  }, [user?.role]);

  const isAdmin = useMemo(() => {
    const role = user?.role;
    if (!role) return false;
    return String(role).trim() === "USER_ROLE_ADMIN";
  }, [user?.role]);

  const isLoading = isMeLoading || (shouldFetchActiveOrg && isOrgLoading);

  const refetch = useCallback(() => {
    refetchMe();
    if (shouldFetchActiveOrg) {
      refetchOrg();
    }
  }, [refetchMe, shouldFetchActiveOrg, refetchOrg]);

  return {
    user,
    organization: user?.ownerOrganization,
    activeCompany,
    activeCompanyId,
    isReceiverUser,
    isAdmin,
    isImpersonating,
    stopImpersonate,
    isLoading,
    isError: !!meError,
    refetch,
  };
}
